import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './PartnerMeetingDisplay.css';
import { getTodayYYYYMMDD } from '../../utils/dateUtils';

function toYYYYMMDD(value) {
  if (!value) return '';
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const DEFAULT_PARTNERS = [
  'LAXMIKANTH CHINCHURE',
  'GURURAJ CHINCHURE',
  'SHRISHAIL MANTAGI',
  'AKASH BIRAJDAR',
  'MALLIKARJUN CHINCHURE',
  'SHARANU KUMBAR',
  'SIDDARAM DHAGE',
  'VAIJUNATH KORE',
  'GURUSHANTH WADAGAON',
  'MAHESH HADAPAD',
  'SADANAND KARAGIR',
  'GURU BANDE',
];

const API_BASE_URL =
  (process.env.REACT_APP_API_BASE_URL && process.env.REACT_APP_API_BASE_URL.trim()) ||
  (typeof window !== 'undefined' ? window.location.origin : '');

const PartnerAccount = () => {
  const [availablePartners, setAvailablePartners] = useState(DEFAULT_PARTNERS);
  const [startDate, setStartDate] = useState(getTodayYYYYMMDD());
  const [endDate, setEndDate] = useState(getTodayYYYYMMDD());
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const partnersList = useMemo(() => {
    const deduped = new Set();
    return availablePartners
      .map((name) => (typeof name === 'string' ? name.trim() : ''))
      .filter((name) => {
        if (!name) return false;
        const key = name.toUpperCase();
        if (deduped.has(key)) return false;
        deduped.add(key);
        return true;
      });
  }, [availablePartners]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');

    if (partnersList.length === 0) {
      setLoading(false);
      setError('No partners available.');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/partner-account-data`,
        {
          partners: partnersList,
          startDate: toYYYYMMDD(startDate),
          endDate: toYYYYMMDD(endDate),
        },
        { timeout: 60000 }
      );

      setTableData(response.data || []);
    } catch (err) {
      console.error('Error fetching partner account data:', err);
      const message =
        err.response?.data?.error ||
        err.message ||
        'Failed to fetch partner account data. Please try again.';
      if (err.code === 'ECONNABORTED') {
        setError('Request timed out. Check that the backend is running and try again.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [partnersList, startDate, endDate]);

  useEffect(() => {
    let isMounted = true;

    const loadPartners = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/partners`);
        const partnerList = Array.isArray(response.data)
          ? response.data.map((name) => (typeof name === 'string' ? name.trim() : '')).filter(Boolean)
          : [];

        if (!isMounted) return;

        if (partnerList.length > 0) {
          setAvailablePartners(partnerList);
        } else {
          setAvailablePartners(DEFAULT_PARTNERS);
        }
      } catch (err) {
        console.error('Error loading partner names:', err);
        if (isMounted) {
          setAvailablePartners(DEFAULT_PARTNERS);
        }
      }
    };

    loadPartners();

    return () => {
      isMounted = false;
    };
  }, []);

  const toNumber = (value) => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const basePartners = useMemo(() => {
    if (partnersList.length > 0) return partnersList;
    if (availablePartners.length > 0) return availablePartners;
    return DEFAULT_PARTNERS;
  }, [partnersList, availablePartners]);

  const displayRows = useMemo(() => {
    const partnerKeys = new Set(basePartners.map((name) => name.trim().toUpperCase()));
    const dataMap = new Map();

    tableData.forEach((row) => {
      const key = row.partnerName ? row.partnerName.trim().toUpperCase() : '';
      if (key) dataMap.set(key, row);
    });

    const partnerRows = basePartners.map((name) => {
      const key = name.trim().toUpperCase();
      const existingRow = dataMap.get(key);
      if (existingRow) return existingRow;
      return { partnerName: name, md: 0 };
    });

    const extraRows = tableData.filter((row) => {
      const key = row.partnerName ? row.partnerName.trim().toUpperCase() : '';
      return key && !partnerKeys.has(key);
    });

    return [...partnerRows, ...extraRows];
  }, [basePartners, tableData]);

  const grandTotal = useMemo(
    () => displayRows.reduce((sum, row) => sum + toNumber(row.md), 0),
    [displayRows]
  );

  const handlePrint = () => {
    const printContent = document.getElementById('partner-account-table').innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Partner Account</title>
          <style>
            body { font-family: Arial, sans-serif; }
            h2 { margin: 0 0 8px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 16px; }
            th, td { border: 1px solid #ccc; padding: 10px; text-align: left; }
            th { background-color: #f4f4f4; font-weight: bold; }
            .font-bold { font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Partner Account (MD)</h2>
          <p>Date Range: ${startDate} to ${endDate}</p>
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="container">
      <h2 className="title">Partner Account</h2>
      <p style={{ textAlign: 'center', color: '#666', marginTop: '-10px', marginBottom: '20px' }}>
        MD amount (30% of GL, CD & TBD overdue payments collected)
      </p>

      <div className="controls">
        <div className="date-input">
          <label>Start Date:</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>

        <div className="date-input">
          <label>End Date:</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>

        <button onClick={fetchData} className="fetch-button">Fetch Data</button>
      </div>

      {loading ? (
        <div className="loading">Loading data...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div id="partner-account-table" className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Partner Name</th>
                <th>MD Amount</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row, index) => (
                <tr key={index}>
                  <td>{row.partnerName || 'N/A'}</td>
                  <td>{toNumber(row.md).toFixed(2)}</td>
                </tr>
              ))}

              <tr className="font-bold">
                <td>Grand Total</td>
                <td>{grandTotal.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <button onClick={handlePrint} className="print-button">Print Table</button>
    </div>
  );
};

export default PartnerAccount;
